/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import FilterUtils from '../utils/FilterUtils';
import api from './api';
import { GLOBAL_OVERVIEW_SHIFT_ROUTES, GLOBAL_OVERVIEW_SHIFT_STATUS } from '../constants/ShiftStatus';
import { GLOBAL_CALENDAR_SHIFT_TYPES } from '../constants';

const globalFiltersInitialState = {
	resourceTypes: [],
	units: [],
	statuses: FilterUtils.convertGlobalOverviewShiftStatusToOptions(GLOBAL_OVERVIEW_SHIFT_STATUS),
	shiftRoutes: FilterUtils.convertGlobalOverviewShiftStatusToOptions(GLOBAL_OVERVIEW_SHIFT_ROUTES),
	selectedCaregiverFilter: null,
	features: []
};

const emptyResponseHandler = async (response) => {
	const responseText = await response.text();
	return responseText === 'OK' ? responseText : JSON.parse(responseText);
};

const apiWithTags = api.enhanceEndpoints({
	addTagTypes: ['GlobalOverviewShifts', 'GlobalOverviewShiftStructures', 'SlotSegments']
});

const globalOverviewAPI = apiWithTags.injectEndpoints({
	tagTypes: ['GlobalOverviewShifts', 'GlobalOverviewShiftStructures', 'SlotSegments'],
	endpoints: (build) => ({
		shifts: build.query({
			query: ({ start, end, type = GLOBAL_CALENDAR_SHIFT_TYPES.ALL }) =>
				`/flex/global-overview/shifts?start=${start}&end=${end}&type=${type}`,
			providesTags: ['GlobalOverviewShifts']
		}),
		shiftStructures: build.query({
			query: ({ type = GLOBAL_CALENDAR_SHIFT_TYPES.ALL }) => `/flex/global-overview/shift-structures?type=${type}`,
			providesTags: ['GlobalOverviewShifts']
		}),
		reopenShift: build.mutation({
			query: ({ slot }) => ({
				url: `/flex/slot/${slot.slot_id}/reopen`,
				method: 'PUT',
				headers: {
					'management-company-id': slot.management_company_id
				},
				responseHandler: emptyResponseHandler
			}),
			invalidatesTags: ['GlobalOverviewShifts']
		}),
		cancelSlot: build.mutation({
			query: ({ shiftType, slotId, ...payloadBody }) => ({
				url: shiftType === GLOBAL_CALENDAR_SHIFT_TYPES.FLEX ? `/flex/slot/${slotId}/cancel` : `/slot/${slotId}/cancel`,
				method: 'PUT',
				body: payloadBody,
				responseHandler: emptyResponseHandler
			}),
			invalidatesTags: ['GlobalOverviewShifts']
		}),
		recurringOpenSlots: build.mutation({
			query: ({ shiftId, shiftDate, isRecurring = true }) => ({
				url: `/recurring-shifts/${shiftId}/open-slots`,
				method: 'POST',
				body: { isRecurring, shiftDate }
			}),
			invalidatesTags: (_result, error) => (error?.data?.errors[0]?.field === 'shiftId' ? ['GlobalOverviewShifts'] : [])
		}),
		routeSlot: build.mutation({
			query: ({ slot }) => ({
				url: `/flex/slot/${slot.slot_id}/route-slot`,
				method: 'PUT',
				body: {
					oldUnitId: slot.unit_id
				},
				responseHandler: emptyResponseHandler
			}),
			invalidatesTags: ['GlobalOverviewShifts']
		}),
		facilityFloatSlot: build.mutation({
			query: ({ slot, targetFacilityId, caregiverId, structureId, unitId }) => ({
				url: `/flex/management-companies/${slot.management_company_id}/slots/${slot.slot_id}/facility-float`,
				method: 'POST',
				headers: {
					'management-company-id': slot.management_company_id
				},
				body: {
					targetFacilityId,
					caregiverId,
					structureId,
					unitId
				}
			}),
			invalidatesTags: ['GlobalOverviewShifts']
		}),
		getFacilitiesToFloatSlot: build.query({
			query: ({ slotId, managementCompanyId }) => ({
				url: `/flex/management-companies/${managementCompanyId}/slots/${slotId}/float-available-facilities`,
				method: 'GET',
				headers: {
					'management-company-id': managementCompanyId
				}
			}),
			invalidatesTags: ['GlobalOverviewShifts']
		}),
		markSlotAsCallBack: build.mutation({
			query: ({ slotId, facilityId }) => ({
				url: `/on-call/facility/${facilityId}/slots/${slotId}/callback`,
				method: 'PUT',
				responseHandler: emptyResponseHandler
			}),
			invalidatesTags: ['GlobalOverviewShifts']
		}),
		getSlotSegments: build.query({
			query: ({ slotId, managementCompanyId }) => ({
				url: `/flex/slot/${slotId}/segments`,
				method: 'GET',
				headers: {
					'management-company-id': managementCompanyId
				}
			}),
			providesTags: (_result, _error, { slotId }) => [{ type: 'SlotSegments', id: slotId }]
		}),
		splitShift: build.mutation({
			query: ({ slot, segments }) => ({
				url: `/flex/slot/${slot.slot_id}/split`,
				method: 'POST',
				headers: {
					'management-company-id': slot.management_company_id
				},
				body: { segments }
			}),
			invalidatesTags: (_result, _error, { slot }) => [
				'GlobalOverviewShifts',
				{ type: 'SlotSegments', id: slot.slot_id }
			]
		}),
		updateSegmentUnits: build.mutation({
			query: ({ slot, segments }) => ({
				url: `/flex/slot/${slot.slot_id}/segment-units`,
				method: 'PUT',
				headers: {
					'management-company-id': slot.management_company_id
				},
				body: { segments }
			}),
			invalidatesTags: (_result, _error, { slot }) => [
				'GlobalOverviewShifts',
				{ type: 'SlotSegments', id: slot.slot_id }
			]
		}),
		createFlexShifts: build.mutation({
			query: ({ facilityId, structureId, slotCount, startDate }) => ({
				url: `/flex/facility/${facilityId}/shifts`,
				method: 'POST',
				body: [
					{
						structure_id: structureId,
						slots: slotCount,
						unit_id: null,
						start_date: startDate
					}
				],
				responseHandler: emptyResponseHandler
			}),
			invalidatesTags: (result, errors) => (result && !errors ? ['GlobalOverviewShifts'] : [])
		}),
		createOnDemandSlots: build.mutation({
			query: ({ shiftId, numberOfSlots, isRecurring, shiftDate }) => ({
				url: '/slots',
				method: 'POST',
				body: {
					shiftId,
					numberOfSlots,
					isRecurring,
					shiftDate
				}
			}),
			invalidatesTags: (result, errors) => (result && !errors ? ['GlobalOverviewShifts'] : [])
		}),
		createOnDemandShift: build.mutation({
			query: ({ facilityId, slotCount, shiftDate, shiftStructureId }) => ({
				url: '/shifts',
				method: 'POST',
				body: {
					facilityId,
					shiftsData: [
						{
							start_date: shiftDate,
							slots: slotCount,
							unit_id: null,
							structure_id: shiftStructureId
						}
					]
				}
			}),
			invalidatesTags: (result, errors) => (result && !errors ? ['GlobalOverviewShifts'] : [])
		}),
		createHybridShift: build.mutation({
			query: ({ facilityId, slotCount, shiftDate, shiftStructureId }) => ({
				url: `/hybrid/facility/${facilityId}/shifts`,
				method: 'POST',
				body: [
					{
						start_date: shiftDate,
						slots: slotCount,
						unit_id: null,
						structure_id: shiftStructureId
					}
				],
				responseHandler: emptyResponseHandler
			}),
			invalidatesTags: (result, errors) => (result && !errors ? ['GlobalOverviewShifts'] : [])
		}),
		createHybridSlots: build.mutation({
			query: ({ shiftId, numberOfSlots, shiftDate, isRecurring }) => ({
				url: `/hybrid/slots`,
				method: 'POST',
				body: {
					shiftId,
					numberOfSlots,
					isRecurring,
					shiftDate
				},
				responseHandler: emptyResponseHandler
			}),
			invalidatesTags: (result, errors) => (result && !errors ? ['GlobalOverviewShifts'] : [])
		}),
		confirmSlot: build.mutation({
			query: ({ slotId }) => ({
				url: `/slot/${slotId}/confirm`,
				method: 'PUT',
				responseHandler: emptyResponseHandler
			}),
			invalidatesTags: (_result, error) => (error ? [] : ['GlobalOverviewShifts'])
		})
	})
});

export const {
	useShiftsQuery,
	useReopenShiftMutation,
	useShiftStructuresQuery,
	useCancelSlotMutation,
	useRecurringOpenSlotsMutation,
	useRouteSlotMutation,
	useFacilityFloatSlotMutation,
	useGetFacilitiesToFloatSlotQuery,
	useMarkSlotAsCallBackMutation,
	useSplitShiftMutation,
	useUpdateSegmentUnitsMutation,
	useGetSlotSegmentsQuery,
	unwrapError,
	useCreateFlexShiftsMutation,
	useCreateOnDemandSlotsMutation,
	useCreateOnDemandShiftMutation,
	useCreateHybridShiftMutation,
	useCreateHybridSlotsMutation,
	useConfirmSlotMutation
} = globalOverviewAPI;

export const slice = createSlice({
	name: 'globalOverview',
	initialState: {
		periodIndex: 0,
		selectedFacilities: [],
		globalFilters: globalFiltersInitialState
	},
	reducers: {
		savePeriodIndex: (state, action) => {
			state.periodIndex = action.payload;
		},
		saveSelectedFacilities: (state, action) => {
			state.selectedFacilities = action.payload;
		},
		saveFilters: (state, action) => {
			state.globalFilters = action.payload;
		}
	}
});

export const resetApiState = () => api.util.resetApiState();

export const invalidateSlotSegments = (slotId) =>
	api.util.invalidateTags(['GlobalOverviewShifts', { type: 'SlotSegments', id: slotId }]);

export const globalOverviewState = (state) => state.globalOverview;

export const { savePeriodIndex, saveSelectedFacilities, saveFilters } = slice.actions;

export default slice.reducer;
